import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ColumnDef} from "@tanstack/react-table";
import dayjs from "dayjs";
import {DataTable} from "@/lib/components/DataTable";
import {QuoteDetailDTO} from "@/api/data-contracts";
import {CommonI18N, ReportI18N} from "@/translations";
import {DateFormatter} from "@/lib/components/DateFormate";

interface QuotesDetailReportProps {
    data: QuoteDetailDTO[];
}

interface QuotesDetailRow extends QuoteDetailDTO {
    id: number;
}

/**
 * Report table showing detail rows for all quotes in the selected date range.
 */
const QuotesDetailReport: React.FC<QuotesDetailReportProps> = ({ data }) => {
    const { t } = useTranslation();
    const dateFormat = DateFormatter("date");

    const rows = useMemo<QuotesDetailRow[]>(
        () => data.map((item, index) => ({ ...item, id: index })),
        [data]
    );

    const columns = useMemo<ColumnDef<QuotesDetailRow>[]>(
        () => [
            {
                header: t(ReportI18N.page.column.company),
                accessorFn: (row) => row.supplierCompanyName ?? "",
                id: "supplierCompanyName"
            },
            {
                header: t(ReportI18N.page.column.companyNumber),
                accessorFn: (row) => row.supplierCompanyId ?? "",
                id: "supplierCompanyId"
            },
            {
                header: t(ReportI18N.page.column.status),
                accessorFn: (row) => (row.status ? t(CommonI18N.enum.quoteStatus[row.status]) : ""),
                id: "status"
            },
            {
                header: t(ReportI18N.page.column.statusDate),
                accessorFn: (row) => (row.statusDate ? dayjs(row.statusDate).format(dateFormat) : ""),
                id: "statusDate"
            },
            {
                header: t(ReportI18N.page.column.quoteReferenceNumber),
                accessorFn: (row) => row.quoteCustomerReferenceNumber ?? "–",
                id: "quoteCustomerReferenceNumber"
            },
            {
                header: t(ReportI18N.page.column.requestReferenceNumber),
                accessorFn: (row) => row.requestCustomerReferenceNumber ?? "–",
                id: "requestCustomerReferenceNumber"
            }
        ],
        [t, dateFormat]
    );

    return <DataTable columns={columns} data={rows} noResultsValue="" striped maxHeight={940} />;
};

export default QuotesDetailReport;
